import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Wallet, ListChecks, Share2, Download, Save, Utensils, Bed, Star, Languages } from "lucide-react";
import DayCard from "./DayCard";
import HotelCard from "./HotelCard";
import RestaurantCard from "./RestaurantCard";
import BudgetCard from "./BudgetCard";
import ChecklistCard from "./ChecklistCard";
import MapView from "./MapView";
import { openMagazinePrint } from "../lib/pdf";
import { translateTrip } from "../lib/api";
import { toast } from "sonner";
import { useT } from "../lib/i18n";
import { track } from "../lib/analytics";

export default function ItineraryView({ trip: originalTrip, onSave, sessionId }) {
  const { t, lang } = useT();
  const [displayTrip, setDisplayTrip] = useState(originalTrip);
  const [tripLang, setTripLang] = useState("original"); // "original" | "en" | "ms"
  const [cache, setCache] = useState({}); // { en: {...}, ms: {...} }
  const [translating, setTranslating] = useState(false);

  const trip = displayTrip;
  if (!trip) return null;

  const total = trip.budget_breakdown?.total ?? trip.budget_myr ?? 0;

  const doTranslate = async (target) => {
    if (target === tripLang) return;
    if (target === "original") {
      setDisplayTrip(originalTrip);
      setTripLang("original");
      return;
    }
    if (cache[target]) {
      setDisplayTrip(cache[target]);
      setTripLang(target);
      return;
    }
    setTranslating(true);
    try {
      const res = await translateTrip(originalTrip, target);
      setCache((c) => ({ ...c, [target]: res.trip }));
      setDisplayTrip(res.trip);
      setTripLang(target);
    } catch (e) {
      toast.error(t("iv.translation_failed"));
    } finally { setTranslating(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-6 sm:space-y-7"
      data-testid="itinerary-view"
    >
      {/* Header card */}
      <div className="bg-gradient-to-br from-[#0B1F4D] via-[#123B8F] to-[#0A2540] text-white rounded-[1.75rem] p-5 sm:p-7 md:p-8 shadow-[0_18px_56px_rgba(10,37,64,0.24)] relative overflow-hidden border border-[#F3E3B2]/15">
        <div className="absolute -right-8 -top-8 w-48 h-48 rounded-full bg-gradient-to-br from-[#4FB3E8]/30 to-transparent blur-2xl" />
        <div className="absolute -left-12 bottom-0 w-56 h-56 rounded-full bg-[#15C3C6]/15 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center rounded-full border border-[#F3E3B2]/25 bg-[#F3E3B2]/10 px-3 py-1 text-[10px] sm:text-xs uppercase tracking-[0.18em] text-[#F3E3B2] mb-3">{t("planning.ready")}</div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#AFC9FF] mb-2">{t("book.title")}</div>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight leading-tight">{trip.destination}</h2>
          <p className="text-sm text-white/70 mt-1">{trip.country} · {trip.days} {t("iv.days")} · {trip.party}</p>
          {trip.summary && <p className="text-[15px] text-white/85 mt-4 max-w-2xl leading-relaxed">{trip.summary}</p>}
          <div className="mt-5 flex flex-wrap gap-2">
            {trip.trip_type && <Chip>{trip.trip_type}</Chip>}
            {trip.muslim_friendly && <Chip>🕌 Muslim Friendly</Chip>}
            <Chip>{t("iv.budget")} ~ RM{total.toLocaleString()}</Chip>
          </div>
          <div className="mt-6 grid grid-cols-1 min-[420px]:grid-cols-2 sm:flex sm:flex-wrap gap-2.5 no-print">
            <ActionBtn onClick={onSave} icon={<Save className="w-4 h-4" />} label={t("book.save")} testid="save-trip-btn" primary />
            <ActionBtn
              onClick={() => {
                openMagazinePrint(trip);
                track("travel_book_pdf_downloaded", { destination: trip.destination, days: trip.days });
              }}
              icon={<Download className="w-4 h-4" />}
              label={t("book.download")}
              testid="pdf-btn"
            />
            <ActionBtn onClick={() => {
              const text = `${t("book.title")}: ${trip.destination} · ${trip.days} ${t("iv.days")} via TAi-P`;
              if (navigator.share) navigator.share({ title: text, text });
              else navigator.clipboard.writeText(window.location.href);
            }} icon={<Share2 className="w-4 h-4" />} label={t("iv.share")} testid="share-btn" />
          </div>
        </div>
      </div>

      {/* Days */}
      <SectionTitle icon={<MapPin className="w-4 h-4" />}>{t("iv.daily_itinerary")}</SectionTitle>
      <div className="space-y-4">
        {(trip.days_plan || []).map((d, i) => (
          <DayCard key={i} day={d} />
        ))}
      </div>

      {/* Hotels */}
      {trip.hotels?.length > 0 && (
        <>
          <SectionTitle icon={<Bed className="w-4 h-4" />}>{t("iv.hotels")} <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-[#F5F5F7] text-gray-500 font-normal">{t("iv.demo_badge")}</span></SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {trip.hotels.map((h, i) => <HotelCard key={i} hotel={h} />)}
          </div>
        </>
      )}

      {/* Map */}
      {trip.map_center && (
        <>
          <SectionTitle icon={<MapPin className="w-4 h-4" />}>{t("iv.map")}</SectionTitle>
          <MapView trip={trip} />
        </>
      )}

      {/* Restaurants */}
      {trip.restaurants?.length > 0 && (
        <>
          <SectionTitle icon={<Utensils className="w-4 h-4" />}>{t("iv.restaurants")}</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {trip.restaurants.map((r, i) => <RestaurantCard key={i} r={r} />)}
          </div>
        </>
      )}

      {/* Budget */}
      {trip.budget_breakdown && (
        <>
          <SectionTitle icon={<Wallet className="w-4 h-4" />}>{t("iv.budget")}</SectionTitle>
          <BudgetCard budget={trip.budget_breakdown} />
        </>
      )}

      {/* Checklist */}
      {trip.checklist?.length > 0 && (
        <>
          <SectionTitle icon={<ListChecks className="w-4 h-4" />}>{t("iv.checklist")}</SectionTitle>
          <ChecklistCard items={trip.checklist} />
        </>
      )}

      {/* Tips */}
      {trip.tips?.length > 0 && (
        <div className="bg-[#FFFCF3] border border-[#F1E5C2] rounded-[1.5rem] p-5">
          <div className="text-sm font-semibold text-[#0A2540] mb-2 flex items-center gap-2"><Star className="w-4 h-4 text-[#D4AF37]" /> {t("iv.tips")}</div>
          <ul className="space-y-1.5 text-sm text-[#1D1D1F]">
            {trip.tips.map((tip, i) => <li key={i} className="pl-4 relative before:content-['·'] before:absolute before:left-0 before:text-[#D4AF37]">{tip}</li>)}
          </ul>
        </div>
      )}

      <div className="text-[11px] text-gray-400 text-center pt-2">
        {t("iv.disclaimer_footer")}
      </div>
    </motion.div>
  );
}

function SectionTitle({ children, icon }) {
  return (
    <h3 className="flex items-center gap-2 text-lg font-semibold text-[#0A2540] mt-2">
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#F5F5F7] text-[#0A2540]">{icon}</span>
      {children}
    </h3>
  );
}

function Chip({ children }) {
  return <span className="text-xs px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/15">{children}</span>;
}

function ActionBtn({ onClick, icon, label, primary, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={
        primary
          ? "chip justify-center bg-gradient-to-r from-[#F3E3B2] to-[#D6B36A] hover:brightness-105 text-[#0B1F4D] px-4 py-2.5 rounded-full text-sm font-semibold flex items-center gap-2 shadow-[0_6px_18px_rgba(214,179,106,0.2)]"
          : "chip justify-center bg-white/10 hover:bg-white/16 border border-white/20 text-white px-4 py-2.5 rounded-full text-sm font-medium flex items-center gap-2"
      }
    >
      {icon}
      {label}
    </button>
  );
}
